import React, { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "/api";

// ─── Design tokens (mirror StrategyPrompt) ──────────────────────────────────
const T = {
  bg: "#eef2f7", card: "#ffffff", border: "#e8edf3",
  text: "#0f172a", sub: "#64748b", muted: "#94a3b8",
  blue: "#2563eb", indigo: "#4f46e5", red: "#ef4444", green: "#10b981", field: "#f8fafc",
};

const Card = ({ children, style }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 22, marginBottom: 16, boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 10px 30px rgba(15,23,42,0.035)", ...style }}>{children}</div>
);

const Pill = ({ children, tone = "muted" }) => {
  const tones = {
    green: { bg: "#ecfdf5", cl: "#059669", bd: "#a7f3d0" },
    red: { bg: "#fef2f2", cl: "#dc2626", bd: "#fecaca" },
    blue: { bg: "#eff6ff", cl: "#2563eb", bd: "#bfdbfe" },
    muted: { bg: "#f1f5f9", cl: "#64748b", bd: "#e2e8f0" },
  }[tone];
  return <span style={{ display: "inline-block", fontSize: 11.5, fontWeight: 700, color: tones.cl, background: tones.bg, border: `1px solid ${tones.bd}`, borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap" }}>{children}</span>;
};

const actionTone = (action) => action === "BUY_CE" ? "green" : action === "BUY_PE" ? "red" : action === "ERROR" ? "red" : "muted";
const actionLabel = (action) => action === "BUY_CE" ? "BUY CE" : action === "BUY_PE" ? "BUY PE" : action === "ERROR" ? "ERROR" : "NO TRADE";

export default function AiAgentLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    axios.get(`${API}/ai-agent/decisions?limit=60`, { headers: token ? { Authorization: "Bearer " + token } : {} })
      .then((res) => { if (!cancelled) setRows(res.data?.decisions || []); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.message || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const tradedCount = rows.filter((r) => r.traded).length;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 18, alignItems: "flex-start" }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg,#eef2ff,#dbeafe)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0 }}>🤖</div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>AI Trader Agent (1-lot test)</div>
          <div style={{ fontSize: 12.5, color: T.muted, marginTop: 2, lineHeight: 1.5 }}>
            Daily rule-based read of the pre-market mood — trades only on a high-conviction bullish/bearish verdict, 1 lot only. Every day is logged here, including NO TRADE days.
          </div>
        </div>
      </div>

      <Card style={{ display: "flex", gap: 24, alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>Days logged</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{rows.length}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>Trades taken</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{tradedCount}</div>
        </div>
        <div style={{ fontSize: 12, color: T.sub, marginLeft: "auto" }}>
          Open/closed trade details are on the <b>Positions</b> and <b>History</b> pages under the name "AI Trader Agent (1-lot test)".
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {loading && <div style={{ padding: 22, color: T.muted, fontSize: 13 }}>Loading…</div>}
        {!loading && err && <div style={{ padding: 22, color: T.red, fontSize: 13 }}>Failed to load: {err}</div>}
        {!loading && !err && !rows.length && <div style={{ padding: 22, color: T.muted, fontSize: 13 }}>No decisions logged yet — the agent runs each weekday morning at 08:50 IST.</div>}
        {!loading && !err && !!rows.length && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: T.field, textAlign: "left" }}>
                {["Date", "Action", "Edge", "SL%", "Target%", "Reasoning"].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", fontSize: 10.5, fontWeight: 700, color: T.sub, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${T.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: T.text, fontWeight: 600 }}>{r.date}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <Pill tone={actionTone(r.action)}>{actionLabel(r.action)}</Pill>
                    {r.traded && <span style={{ marginLeft: 6 }}><Pill tone="blue">traded</Pill></span>}
                  </td>
                  <td style={{ padding: "10px 14px", color: T.sub }}>{r.confidence ?? "—"}</td>
                  <td style={{ padding: "10px 14px", color: T.sub }}>{r.slPct || "—"}</td>
                  <td style={{ padding: "10px 14px", color: T.sub }}>{r.targetPct || "—"}</td>
                  <td style={{ padding: "10px 14px", color: T.sub, maxWidth: 420 }}>{r.error ? <span style={{ color: T.red }}>{r.error}</span> : r.reasoning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
